'use client';

import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import {
	ColorLeetcodeIcon,
	ColorLinkedinIcon,
	ColorTelegramIcon,
	ColorYoutubeIcon,
	PlusIcon,
} from '@/shared';
import { useUpdate } from '@/shared/hooks';

import type { ITeamMember } from '../../types';

const iconMap = {
	telegram: ColorTelegramIcon,
	linkedin: ColorLinkedinIcon,
	youtube: ColorYoutubeIcon,
	leetcode: ColorLeetcodeIcon,
};

const TeamV2Item = ({ item }: { item: ITeamMember }) => {
	const [open, setOpen] = useState(false);

	useUpdate(() => {
		if (!open) {
			return;
		}
		setTimeout(() => {
			window.addEventListener('click', () => setOpen(false), { once: true });
		}, 0);
	}, [open]);

	const frontClassName = clsx('transition-all duration-300 ease-in-out', {
		'group-hover:opacity-0': !open,
		'opacity-0': open,
	});

	const iconClassName = clsx(
		'cursor-pointer rounded-full bg-white opacity-0 transition-all duration-300 ease-in-out',
		{
			'group-hover:opacity-100': !open,
			'opacity-100': open,
		},
	);

	const closeClassName = clsx(
		'size-8 cursor-pointer rounded-full bg-white p-1 transition-all duration-300 ease-in-out 2xl:size-12',
		{
			'group-hover:rotate-45': !open,
			'rotate-45': open,
		},
	);

	return (
		<div className="group relative aspect-[370/400] overflow-hidden rounded-xl border border-[#DBDFE9] bg-gray-5 2xl:rounded-[40px]">
			<dl className="flex flex-col gap-4 p-4 xl:gap-6 xl:rounded-3xl xl:p-6 2xl:gap-8 2xl:p-8">
				<dt className="flex flex-row gap-3 xl:gap-8">
					<Image
						src={item.image}
						alt={item.name}
						loading="lazy"
						width={140}
						height={140}
						className="aspect-square max-w-[100px] rounded-3xl object-cover xl:max-w-[140px]"
					/>
				</dt>
				<dd className="space-y-3 text-sm/[1.2] text-[#4B5675] xl:text-base/[1.6]">
					{item.content}
				</dd>
			</dl>

			{item.imageLarge && (
				<div className={frontClassName}>
					<Image
						src={item.imageLarge}
						alt={item.name}
						loading="lazy"
						width={370}
						height={400}
						className="absolute left-0 top-0 h-full w-full object-cover"
					/>
					<div className="pointer-events-none absolute bottom-0 left-0 flex flex-col gap-3 bg-gradient-to-t from-black to-transparent p-4 xl:p-6 2xl:p-8">
						<div className="text-lg/[1.6] font-semibold text-white xl:text-[32px]/[1.6]">
							{item.name}
						</div>
						<div className="text-base/[1.6] text-white xl:text-2xl/[1.6]">
							{item.position}
						</div>
					</div>
				</div>
			)}

			<div className="absolute right-0 top-0 flex flex-row gap-3 p-4 xl:p-6 2xl:p-8">
				{item.links.map((link, key) => {
					const IconComponent = iconMap[link.icon as keyof typeof iconMap];
					return IconComponent ? (
						<Link
							key={key}
							href={link.href}
							target="_blank"
							rel="noopener noreferrer"
							className={iconClassName}
						>
							<IconComponent className="size-8 2xl:size-12" />
						</Link>
					) : null;
				})}
				<PlusIcon onClick={() => setOpen(true)} className={closeClassName} />
			</div>
		</div>
	);
};

export const TeamV2 = ({ members }: { members: ITeamMember[] }) => {
	const cn = clsx(
		'flex flex-nowrap gap-3 overflow-y-auto',
		'max-lg:-m-3 max-lg:snap-x max-lg:snap-mandatory max-lg:p-3',
		'lg:gap-4 2xl:gap-6',
		{
			'lg:justify-center': members.length < 3,
			'lg:grid lg:grid-cols-3': members.length >= 3,
		},
	);
	return (
		<div className={cn}>
			{members.map((item, key) => (
				<div
					key={key}
					className="w-[90%] max-w-96 flex-shrink-0 snap-center lg:w-full lg:max-w-md 2xl:max-w-xl"
				>
					<TeamV2Item item={item} />
				</div>
			))}
		</div>
	);
};
